import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { MoreHorizontal } from 'lucide-react';

/**
 * Accessible menu. Opens on click, closes on outside click / Escape / blur-out,
 * supports arrow-key roving focus. Panel animates in over 180ms, out instantly —
 * exits should never make the user wait.
 */
const Dropdown = ({
    trigger,
    children,
    align = 'right',
    className = '',
    panelClassName = '',
    label = 'Open menu',
}) => {
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);
    const panelRef = useRef(null);
    const triggerRef = useRef(null);
    const id = useId();

    const close = useCallback((refocus = false) => {
        setOpen(false);
        if (refocus) triggerRef.current?.focus();
    }, []);

    useEffect(() => {
        if (!open) return undefined;

        const onPointerDown = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
        };
        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                close(true);
                return;
            }
            if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
            const items = panelRef.current?.querySelectorAll(
                'a[href], button:not([disabled]), [role="menuitem"]'
            );
            if (!items?.length) return;
            e.preventDefault();
            const list = Array.from(items);
            const idx = list.indexOf(document.activeElement);
            const next =
                e.key === 'ArrowDown'
                    ? list[(idx + 1) % list.length]
                    : list[(idx - 1 + list.length) % list.length];
            next?.focus();
        };

        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open, close]);

    return (
        <div className={`relative ${className}`} ref={rootRef}>
            {trigger ? (
                React.cloneElement(trigger, {
                    ref: triggerRef,
                    onClick: (e) => {
                        trigger.props.onClick?.(e);
                        setOpen((v) => !v);
                    },
                    'aria-haspopup': 'menu',
                    'aria-expanded': open,
                    'aria-controls': open ? id : undefined,
                })
            ) : (
                <button
                    ref={triggerRef}
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    aria-haspopup="menu"
                    aria-expanded={open}
                    aria-controls={open ? id : undefined}
                    aria-label={label}
                    className="btn btn-ghost btn-icon btn-sm"
                >
                    <MoreHorizontal size={16} aria-hidden="true" />
                </button>
            )}

            {open && (
                <div
                    id={id}
                    ref={panelRef}
                    role="menu"
                    className={`menu-panel absolute z-dropdown mt-1.5 min-w-[180px] animate-scale-in origin-top ${align === 'right' ? 'right-0' : 'left-0'
                        } ${panelClassName}`}
                    onClick={(e) => {
                        // Close after activating any item, but leave inputs alone.
                        if (e.target.closest('a, button, [role="menuitem"]')) setOpen(false);
                    }}
                >
                    {children}
                </div>
            )}
        </div>
    );
};

export const MenuItem = ({ icon: Icon, children, danger = false, as: Tag = 'button', ...props }) => (
    <Tag
        role="menuitem"
        type={Tag === 'button' ? 'button' : undefined}
        className={`menu-item ${danger ? 'menu-item-danger' : ''}`}
        {...props}
    >
        {Icon && <Icon size={14} aria-hidden="true" className="shrink-0" />}
        <span className="truncate">{children}</span>
    </Tag>
);

export const MenuSeparator = () => <div className="menu-separator" role="separator" />;
export const MenuLabel = ({ children }) => <div className="menu-label">{children}</div>;

export default Dropdown;
